import React, { useEffect, useState, memo, useCallback, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  ArrowRight, BookOpen, Users, Award, Star, Sparkles, Bell, 
  GraduationCap, Lightbulb, Play, Shield, Zap, Target, 
  TrendingUp, Clock, CheckCircle, Rocket, Code, Server, 
  Cloud, Lock, Terminal, ChevronRight, Quote, Calendar, MapPin
} from 'lucide-react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import SEO, { PageSEO } from '../components/SEO';

import { API } from '../config/api';

// Lazy load heavy components that aren't immediately visible
const Footer = lazy(() => import('../components/Footer'));
const CourseCard = lazy(() => import('../components/CourseCard'));
const AnimatedTerminal = lazy(() => import('../components/AnimatedTerminal'));
const ReviewCarousel = lazy(() => import('../components/ReviewCarousel'));

// Simple loading placeholder
const LoadingPlaceholder = () => (
  <div className="animate-pulse bg-slate-800/50 rounded-lg h-48 w-full" />
);

// Reduced motion preference check for accessibility & performance
const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Simplified gradient orbs - use CSS animations for better performance
const GradientOrb = memo(({ className }) => (
  <div
    className={`absolute rounded-full blur-3xl animate-pulse-slow ${className}`}
    style={{ opacity: 0.3 }}
  />
));

// Simplified floating particles - only render if reduced motion not preferred
const FloatingParticle = memo(({ size, left, top }) => {
  if (prefersReducedMotion) return null;
  return (
    <div
      className="absolute rounded-full bg-white/20 animate-float"
      style={{ width: size, height: size, left: `${left}%`, top: `${top}%` }}
    />
  );
});

// Animated counter component - optimized with useCallback
const AnimatedCounter = memo(({ end, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(() => prefersReducedMotion ? end : 0);
  const hasAnimatedRef = React.useRef(false);
  
  useEffect(() => {
    // Skip if reduced motion preferred or already animated
    if (prefersReducedMotion || hasAnimatedRef.current) {
      return;
    }
    
    let startTime;
    let animationId;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        hasAnimatedRef.current = true;
      }
    };
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [end, duration]);
  
  return <span>{count.toLocaleString()}{suffix}</span>;
});

// Stats data
const PLATFORM_STATS = [
  { value: 'Growing', suffix: '', label: 'Community', icon: Users },
  { value: 50, suffix: '+', label: 'Expert Courses', icon: BookOpen },
  { value: 95, suffix: '%', label: 'Success Rate', icon: Target },
  { value: 24, suffix: '/7', label: 'Lab Access', icon: Clock },
];

// Learning paths data
const LEARNING_PATHS = [
  { 
    icon: Shield, 
    title: 'Cybersecurity', 
    description: 'Master security fundamentals, ethical hacking, and incident response',
    color: 'from-red-500 to-orange-500',
    courses: 12
  },
  { 
    icon: Cloud, 
    title: 'Cloud Computing', 
    description: 'AWS, Azure, and Google Cloud certifications and hands-on labs',
    color: 'from-blue-500 to-cyan-500',
    courses: 15
  },
  { 
    icon: Server, 
    title: 'System Administration', 
    description: 'Windows Server, Linux, and enterprise infrastructure management',
    color: 'from-green-500 to-emerald-500',
    courses: 10
  },
  { 
    icon: Code, 
    title: 'DevOps & Automation', 
    description: 'CI/CD pipelines, containers, and infrastructure as code',
    color: 'from-purple-500 to-pink-500',
    courses: 8
  },
];

// Features data
const FEATURES = [
  {
    icon: Terminal,
    title: 'Real Virtual Labs',
    description: 'Practice on actual enterprise environments with our cloud-based virtual machines.',
    gradient: 'from-cyan-500 to-blue-600'
  },
  {
    icon: GraduationCap,
    title: 'Expert Instructors',
    description: 'Learn from certified professionals with 10+ years of industry experience.',
    gradient: 'from-purple-500 to-pink-600'
  },
  {
    icon: Award,
    title: 'Industry Certifications',
    description: 'Prepare for CompTIA, Cisco, AWS, and Microsoft certifications.',
    gradient: 'from-amber-500 to-orange-600'
  },
  {
    icon: Users,
    title: 'Live Mentorship',
    description: 'Get personalized 1-on-1 guidance from mentors who care about your success.',
    gradient: 'from-emerald-500 to-teal-600'
  },
  {
    icon: Rocket,
    title: 'Career Services',
    description: 'Resume reviews, interview prep, and job placement assistance.',
    gradient: 'from-rose-500 to-red-600'
  },
  {
    icon: Lock,
    title: 'Lifetime Access',
    description: 'Once enrolled, access your courses forever with free updates.',
    gradient: 'from-indigo-500 to-violet-600'
  },
];

// Company logos
const TRUSTED_COMPANIES = ['Microsoft', 'Google', 'Amazon', 'IBM', 'Cisco', 'CompTIA'];

const Home = () => {
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [featuredBootcamps, setFeaturedBootcamps] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  
  // Defer API call to not block initial paint - use requestIdleCallback or setTimeout
  useEffect(() => {
    const fetchCoursesAndBootcamps = async () => {
      try {
        // Fetch LMS courses, legacy courses, bootcamps, and events in parallel
        const [lmsCourseRes, coursesRes, bootcampsRes, eventsRes] = await Promise.all([
          axios.get(`${API}/classroom/courses`, { timeout: 8000 }).catch(() => ({ data: { courses: [] } })),
          axios.get(`${API}/courses`, { timeout: 8000 }).catch(() => ({ data: [] })),
          axios.get(`${API}/bootcamps`, { timeout: 8000 }).catch(() => ({ data: [] })),
          axios.get(`${API}/events`, { timeout: 8000 }).catch(() => ({ data: { events: [] } }))
        ]);
        
        // Combine LMS courses + legacy courses
        const lmsCourses = (lmsCourseRes.data?.courses || []).map(c => ({
          ...c, source: 'lms', is_locked: false,
          banner_url: c.banner_image_url || null,
        }));
        const legacyCourses = (coursesRes.data || []).filter(course => !course.is_locked);
        const allCourses = [...lmsCourses, ...legacyCourses];
        setFeaturedCourses(allCourses.slice(0, 3));
        
        // Get bootcamps (include upcoming, published, active, enrollment_open, in_progress)
        const visibleBootcamps = (bootcampsRes.data || []).filter(
          b => ['published', 'active', 'enrollment_open', 'upcoming', 'in_progress'].includes(b.status)
        );
        setFeaturedBootcamps(visibleBootcamps.slice(0, 3));
        
        // Get upcoming events (published only)
        const eventsData = eventsRes.data?.events || eventsRes.data || [];
        const publishedEvents = eventsData.filter(e => e.status === 'published');
        setUpcomingEvents(publishedEvents.slice(0, 3));
      } catch (error) {
        console.log('Home page courses/bootcamps fetch timeout or error');
      }
    };
    
    // Defer fetch until browser is idle or after 100ms
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => fetchCoursesAndBootcamps(), { timeout: 2000 });
    } else {
      setTimeout(fetchCoursesAndBootcamps, 100);
    }
  }, []);
  
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    setNewsletterLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: newsletterEmail,
        tags: ['newsletter_subscriber', 'homepage_signup']
      });
      if (response.data.success) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        setTimeout(() => setNewsletterSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      alert(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };
  
  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Security Analyst at Microsoft',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
      content: 'SQUIDEMY transformed my career. The hands-on labs and expert instructors helped me land my dream job in cybersecurity.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'Cloud Engineer at AWS',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      content: 'The mentorship program connected me with industry professionals who guided my journey from help desk to cloud engineering.',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      role: 'DevOps Lead at Google',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      content: 'The practical labs and real-world projects gave me the confidence to switch careers. Best investment in my education!',
      rating: 5
    }
  ];
  
  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <SEO {...PageSEO.home} url="https://squidemy.com/" />
      <Navigation />
      
      {/* ============ HERO SECTION ============ */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
          {/* Decorative orbs - use responsive sizing to prevent overflow */}
          <GradientOrb className="w-[50vw] max-w-[600px] h-[50vw] max-h-[600px] bg-purple-600/30 top-[-15%] left-[-15%]" delay={0} />
          <GradientOrb className="w-[40vw] max-w-[500px] h-[40vw] max-h-[500px] bg-cyan-600/20 top-[20%] right-[-10%]" delay={2} />
          <GradientOrb className="w-[30vw] max-w-[400px] h-[30vw] max-h-[400px] bg-pink-600/20 bottom-[-10%] left-[30%]" delay={4} />
          
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBoMXYxaC0xem0wLTMwaDFWMGgtMXptMCA2MGgxdi0xaC0xek0wIDMwaDFWMjlIMHptNjAgMGgxdi0xaC0xeiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvZz48L3N2Zz4=')] opacity-40" />
          
          {/* Floating particles */}
          {[...Array(15)].map((_, i) => (
            <FloatingParticle 
              key={i} 
              delay={i * 0.5} 
              size={4 + Math.random() * 6} 
              left={Math.random() * 100} 
              top={Math.random() * 100}
            />
          ))}
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 rounded-full px-4 py-2 mb-6"
              >
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-purple-300">Join Our Growing IT Community</span>
              </motion.div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight">
                Launch Your
                <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  IT Career
                </span>
                Today
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-xl">
                Master cybersecurity, cloud computing, and system administration with 
                <span className="text-purple-400 font-semibold"> hands-on virtual labs</span>, 
                expert instructors, and industry certifications.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg shadow-purple-500/25 gap-2 text-lg h-14 px-8"
                  onClick={() => navigate('/courses')}
                >
                  <Rocket className="w-5 h-5" />
                  Start Learning Free
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-slate-600 text-white hover:bg-slate-800 gap-2 text-lg h-14 px-8"
                  onClick={() => navigate('/worlds/squidtour')}
                >
                  <Play className="w-5 h-5" />
                  Take the Tour
                </Button>
              </div>
              
              {/* Quick stats */}
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2 text-slate-400">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Cancel anytime</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>24/7 lab access</span>
                </div>
              </div>
            </motion.div>
            
            {/* Right Content - Hero Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              {/* Main card */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-2xl blur-xl opacity-30" />
                <Card className="relative bg-slate-900/80 backdrop-blur-xl border-slate-700/50 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 relative">
                      <Suspense fallback={<LoadingPlaceholder />}>
                        <AnimatedTerminal />
                      </Suspense>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Floating badge cards */}
              <motion.div
                className="absolute -top-4 -right-4 bg-slate-900/90 backdrop-blur-xl border border-slate-700 rounded-xl p-3 shadow-xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">CompTIA Partner</p>
                    <p className="text-sm font-semibold text-white">Official Vouchers</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                className="absolute -bottom-4 -left-4 bg-slate-900/90 backdrop-blur-xl border border-slate-700 rounded-xl p-3 shadow-xl"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Active Community</p>
                    <p className="text-sm font-semibold text-white">Growing Daily</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
          </svg>
        </div>
      </section>
      
      {/* ============ STATS SECTION ============ */}
      <section className="py-12 bg-background relative -mt-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {PLATFORM_STATS.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700 hover:border-purple-500/50 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <stat.icon className="w-8 h-8 mx-auto mb-3 text-purple-400" />
                    <p className="text-3xl md:text-4xl font-bold text-white mb-1">
                      {typeof stat.value === 'number' ? (
                        <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                      ) : (
                        <span>{stat.value}{stat.suffix}</span>
                      )}
                    </p>
                    <p className="text-sm text-slate-400">{stat.label}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* ============ TRUSTED BY SECTION ============ */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-400 mb-8 text-sm uppercase tracking-wider">
            Trusted by professionals at leading companies
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {TRUSTED_COMPANIES.map((company, idx) => (
              <motion.div
                key={company}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.5 }}
                whileHover={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-2xl md:text-3xl font-bold text-slate-500 hover:text-slate-300 transition-colors cursor-default"
              >
                {company}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* ============ LEARNING PATHS SECTION ============ */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/30 mb-4">
              <Target className="w-3 h-3 mr-1" />
              Career Paths
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Choose Your <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Learning Path</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Whether you're starting from scratch or advancing your career, we have the perfect path for you.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {LEARNING_PATHS.map((path, idx) => (
              <motion.div
                key={path.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="group h-full bg-slate-900/50 border-slate-800 hover:border-purple-500/50 transition-all duration-300 cursor-pointer overflow-hidden"
                  onClick={() => navigate('/roadmap')}
                >
                  <CardContent className="p-6 relative">
                    <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${path.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity`} />
                    
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${path.color} flex items-center justify-center mb-4 shadow-lg`}>
                      <path.icon className="w-7 h-7 text-white" />
                    </div>
                    
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-purple-300 transition-colors">
                      {path.title}
                    </h3>
                    <p className="text-sm text-slate-400 mb-4">{path.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">{path.courses} courses</span>
                      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-purple-400 group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* ============ FEATURES SECTION ============ */}
      <section className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 mb-4">
              <Zap className="w-3 h-3 mr-1" />
              Why Choose Us
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Everything You Need to <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Succeed</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We've built the most comprehensive IT learning platform with features that help you learn faster and land your dream job.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-sm text-slate-400">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* ============ FEATURED COURSES SECTION ============ */}
      <section className="py-20 relative overflow-hidden">
        {/* Teal/Cyan gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/50 via-cyan-900/40 to-slate-900" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-500/15 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-500/15 rounded-full blur-[100px]" />
          <div className="absolute top-1/2 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12"
          >
            <div>
              <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/40 mb-4">
                <Rocket className="w-3 h-3 mr-1" /> Featured Programs
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Courses & Bootcamps</h2>
              <p className="text-cyan-100/70 mt-2">Explore our courses and intensive bootcamp programs</p>
            </div>
            <Button 
              variant="outline" 
              className="gap-2 w-fit border-teal-400/50 text-teal-100 hover:bg-teal-500/20" 
              onClick={() => navigate('/courses')}
            >
              View All Programs
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
          
          {(featuredCourses.length > 0 || featuredBootcamps.length > 0) ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Show bootcamps first for prominence */}
              {/* Show LMS courses first, then bootcamps */}
              {featuredCourses.map((course, idx) => (
                <motion.div
                  key={`course-${course.course_id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={course.source === 'lms' ? `/blackboard/${course.course_id}` : `/courses/${course.slug || course.course_id}`}>
                    <Card className="group overflow-hidden border-slate-700/50 bg-slate-800/50 backdrop-blur-sm hover:bg-slate-800/70 hover:border-teal-500/30 transition-all duration-300 h-full cursor-pointer">
                      <div className="relative h-48 bg-gradient-to-br from-purple-600/30 to-teal-500/30 overflow-hidden">
                        {course.banner_url ? (
                          <img src={course.banner_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-16 h-16 text-teal-400/40" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3 flex gap-2">
                          <Badge className="bg-teal-500/90 text-white border-0">Course</Badge>
                          {course.pwyc_enabled && <Badge className="bg-pink-500/90 text-white border-0">PWYC</Badge>}
                        </div>
                      </div>
                      <CardContent className="p-5">
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-teal-400 transition-colors line-clamp-2">
                          {course.title}
                        </h3>
                        <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                          {course.description || course.subtitle}
                        </p>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-3 text-slate-500">
                            {course.duration_weeks && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {course.duration_weeks} Weeks
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-4 h-4" />
                              {course.module_count || course.modules?.length || 0} Modules
                            </span>
                          </div>
                          <span className="font-bold text-teal-400">
                            {course.pwyc_enabled ? 'PWYC' : 'Free'}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
              {/* Then fill remaining with bootcamps */}
              {featuredBootcamps.slice(0, Math.max(0, 3 - featuredCourses.length)).map((bootcamp, idx) => (
                <motion.div
                  key={`bootcamp-${bootcamp.bootcamp_id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={`/bootcamp/${bootcamp.bootcamp_id}`}>
                    <Card className="h-full bg-slate-900/50 border-slate-800 hover:border-teal-500/50 transition-all duration-300 overflow-hidden group">
                      <div className="relative h-48 overflow-hidden">
                        {bootcamp.thumbnail_url ? (
                          <img 
                            src={bootcamp.thumbnail_url} 
                            alt={bootcamp.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                            width="400"
                            height="192"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-teal-600 to-cyan-700 flex items-center justify-center">
                            <GraduationCap className="w-16 h-16 text-white/50" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white border-0 shadow-lg">
                            <Rocket className="w-3 h-3 mr-1" />
                            Bootcamp
                          </Badge>
                        </div>
                        {bootcamp.status === 'enrollment_open' && (
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-green-500/90 text-white border-0 animate-pulse">
                              Enrolling Now
                            </Badge>
                          </div>
                        )}
                        {bootcamp.status === 'upcoming' && (
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-amber-500/90 text-white border-0">
                              Coming Soon
                            </Badge>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-5">
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-teal-400 transition-colors line-clamp-2">
                          {bootcamp.title}
                        </h3>
                        <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                          {bootcamp.description}
                        </p>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-3 text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {bootcamp.duration_days || 30} Days
                            </span>
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-4 h-4" />
                              {bootcamp.modules?.length || 0} Modules
                            </span>
                          </div>
                          <span className="font-bold text-teal-400">
                            {bootcamp.price > 0 ? `$${bootcamp.price}` : 'Free'}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="relative inline-block mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <GraduationCap className="w-12 h-12 text-white" />
                </div>
                <motion.div
                  className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-4 h-4 text-teal-500" />
                </motion.div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Courses Coming Soon!</h3>
              <p className="text-cyan-100/70 max-w-md mx-auto mb-6">
                We're preparing amazing IT courses for you. Sign up to be notified when we launch!
              </p>
              <Button onClick={() => navigate('/contact')} className="gap-2 bg-white text-teal-600 hover:bg-teal-50">
                <Bell className="w-4 h-4" />
                Notify Me
              </Button>
            </motion.div>
          )}
        </div>
      </section>
      
      {/* ============ TESTIMONIALS SECTION ============ */}
      <section className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-pink-500/10 text-pink-400 border-pink-500/30 mb-4">
              <Quote className="w-3 h-3 mr-1" />
              Success Stories
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              What Our <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Students Say</span>
            </h2>
            <p className="text-lg text-muted-foreground">Join thousands of successful IT professionals</p>
          </motion.div>
          
          <Suspense fallback={<LoadingPlaceholder />}>
            <ReviewCarousel />
          </Suspense>
        </div>
      </section>
      
      {/* ============ UPCOMING EVENTS SECTION ============ */}
      {upcomingEvents.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white">Upcoming Events</h2>
                <p className="text-cyan-100/70 mt-2">Join our live workshops and training sessions</p>
              </div>
              <Button 
                variant="outline" 
                className="gap-2 w-fit border-purple-400/50 text-purple-100 hover:bg-purple-500/20" 
                onClick={() => navigate('/events')}
              >
                View All Events
                <ArrowRight className="w-4 h-4" />
              </Button>
            </motion.div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event, idx) => (
                <motion.div
                  key={event.event_id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={`/events/${event.slug}`}>
                    <Card className="h-full bg-slate-900/50 border-slate-800 hover:border-purple-500/50 transition-all duration-300 overflow-hidden group">
                      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-purple-600 to-cyan-700">
                        {event.banner_image_url ? (
                          <img 
                            src={event.banner_image_url} 
                            alt={event.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Calendar className="w-16 h-16 text-white/50" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 shadow-lg">
                            <Calendar className="w-3 h-3 mr-1" />
                            Event
                          </Badge>
                        </div>
                        {event.spots_remaining && event.spots_remaining < 20 && (
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-orange-500/90 text-white border-0">
                              {event.spots_remaining} spots left
                            </Badge>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-bold text-lg text-white mb-2 group-hover:text-purple-300 transition-colors line-clamp-2">
                          {event.title}
                        </h3>
                        <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                          {event.short_description || event.description?.substring(0, 100)}
                        </p>
                        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(event.start_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {new Date(event.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                          {event.level && (
                            <span className="flex items-center gap-1">
                              <Target className="w-3.5 h-3.5" />
                              {event.level}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* ============ CTA SECTION ============ */}
      <section className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-slate-900 to-cyan-900">
          <GradientOrb className="w-[40vw] max-w-[500px] h-[40vw] max-h-[500px] bg-purple-500/30 top-[-10%] right-[-10%]" delay={0} />
          <GradientOrb className="w-[35vw] max-w-[400px] h-[35vw] max-h-[400px] bg-cyan-500/30 bottom-[-10%] left-[-10%]" delay={2} />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Launch Your <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">IT Career</span>?
            </h2>
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Join students who are transforming their careers with SQUIDEMY. 
              Start learning today with our free courses and hands-on labs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-slate-900 hover:bg-slate-100 shadow-xl gap-2 text-lg h-14 px-8"
                onClick={() => navigate('/login')}
              >
                <Rocket className="w-5 h-5" />
                Get Started Free
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 gap-2 text-lg h-14 px-8"
                onClick={() => navigate('/contact')}
              >
                Talk to an Advisor
              </Button>
            </div>
            
            <p className="mt-6 text-sm text-slate-400">
              No credit card required • Free courses included • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* ============ NEWSLETTER SECTION ============ */}
      <section className="py-16 bg-background border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Bell className="w-10 h-10 text-purple-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-3">Stay Updated</h3>
          <p className="text-muted-foreground mb-6">
            Get the latest courses, tech news, and exclusive discounts delivered to your inbox.
          </p>
          
          {newsletterSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 max-w-md mx-auto"
            >
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-green-400 font-semibold">You're on the list!</p>
              <p className="text-sm text-slate-400 mt-1">Thanks for subscribing!</p>
            </motion.div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input 
                type="email" 
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="bg-slate-900 border-slate-700 flex-1"
                required
                disabled={newsletterLoading}
              />
              <Button 
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
                disabled={newsletterLoading}
              >
                {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          )}
        </div>
      </section>
      
      <Suspense fallback={<div className="h-64 bg-slate-900" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Home;
